import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/join-future-world.css';
import { CareerApiError, fetchCareerDetail, fetchCareerList, type CareerEntry } from './joinFutureWorldApi';
import useRevealAnimation from '../hooks/useRevealAnimation';
import useDocumentTitle from '../hooks/useDocumentTitle';

type SupportedLocale = 'zh' | 'en' | 'ja';

const normalizeLanguage = (language?: string | null): SupportedLocale => {
  const normalized = (language ?? '').toLowerCase();
  if (normalized.startsWith('zh')) return 'zh';
  if (normalized.startsWith('ja')) return 'ja';
  return 'en';
};

const JoinFutureWorld: React.FC = () => {
  const { i18n, t } = useTranslation();
  const pageRef = useRef<HTMLDivElement>(null);
  const [careers, setCareers] = useState<CareerEntry[]>([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const locale = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  useRevealAnimation(pageRef);

  useEffect(() => {
    let cancelled = false;

    const loadCareers = async () => {
      setLoading(true);
      setError('');
      try {
        const results = await fetchCareerList(locale);
        if (cancelled) return;
        setCareers(results);

        const routeSlug = params.slug ?? '';
        const matchedFromList = results.find((item) => item.slug === routeSlug);
        if (matchedFromList) {
          setSelectedSlug(matchedFromList.slug);
          return;
        }

        if (routeSlug) {
          try {
            const detail = await fetchCareerDetail(routeSlug, locale);
            if (cancelled) return;
            setSelectedSlug(detail.slug);
            if (!results.some((item) => item.slug === detail.slug)) {
              setCareers((current) => [detail, ...current]);
            }
            return;
          } catch (_detailError) {
            if (cancelled) return;
          }
        }

        const defaultCareer = results.find((item) => item.is_default_display) ?? results[0];
        setSelectedSlug(defaultCareer?.slug || '');
      } catch (loadError) {
        if (cancelled) return;
        setCareers([]);
        setSelectedSlug('');
        setError(loadError instanceof CareerApiError || loadError instanceof Error ? loadError.message : t('JoinFutureWorld.status.errorDescription'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadCareers();
    return () => {
      cancelled = true;
    };
  }, [locale, params.slug, t]);

  useEffect(() => {
    if (!selectedSlug) return;
    if (params.slug === selectedSlug) return;
    navigate(`/contact/join/${selectedSlug}`, { replace: true });
  }, [navigate, params.slug, selectedSlug]);

  const selectedCareer = useMemo(() => {
    if (!careers.length) return null;
    return careers.find((item) => item.slug === selectedSlug) ?? careers[0];
  }, [careers, selectedSlug]);
  useDocumentTitle(selectedCareer?.title || t('JoinFutureWorld.hero.title'));

  return (
    <div className="join-page" ref={pageRef}>
      <section className="join-hero">
        <div className="join-hero-bg">
          <div className="join-grid" />
          <div className="join-glow" />
        </div>
        <div className="section-container join-hero-content">
          <div className="join-hero-copy reveal">
            <span className="join-kicker">{t('JoinFutureWorld.hero.kicker')}</span>
            <h1 className="join-hero-title">{t('JoinFutureWorld.hero.title')}</h1>
            <p className="join-hero-description">{t('JoinFutureWorld.hero.description')}</p>
          </div>
        </div>
      </section>

      <section className="section-container join-main-section">
        {loading ? <div className="join-status-card reveal">{t('JoinFutureWorld.status.loading')}</div> : null}

        {!loading && error ? (
          <div className="join-status-card is-error reveal">
            <h3>{t('JoinFutureWorld.status.errorTitle')}</h3>
            <p>{t('JoinFutureWorld.status.errorDescription')}</p>
          </div>
        ) : null}

        {!loading && !error && !selectedCareer ? (
          <div className="join-status-card reveal">
            <h3>{t('JoinFutureWorld.status.emptyTitle')}</h3>
            <p>{t('JoinFutureWorld.status.emptyDescription')}</p>
          </div>
        ) : null}

        {!loading && !error && selectedCareer ? (
          <>
            <section className="join-switcher-panel reveal">
              <div className="section-header">
                <span className="section-subtitle">{t('JoinFutureWorld.switcher.label')}</span>
                <h2 className="section-title">{t('JoinFutureWorld.switcher.title')}</h2>
              </div>
              <div className="join-position-switcher" role="tablist" aria-label={t('JoinFutureWorld.switcher.title')}>
                {careers.map((career) => {
                  const active = selectedCareer.slug === career.slug;
                  return (
                    <button
                      key={career.slug}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      className={`join-position-chip${active ? ' is-active' : ''}`}
                      onClick={() => setSelectedSlug(career.slug)}
                    >
                      <span>{career.badge}</span>
                      <div className="join-position-chip-copy">
                        <strong>{career.title}</strong>
                        <small>{career.summary}</small>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <div className="join-layout">
              <div className="join-content">
                <section className="join-role-card reveal delay-1">
                  <span className="join-page-tag">{selectedCareer.badge}</span>
                  <h2>{selectedCareer.title}</h2>
                  <p>{selectedCareer.summary}</p>
                  <p className="join-role-meta">
                    <span>{t('JoinFutureWorld.meta.publishedAt')}</span>
                    <strong>{selectedCareer.published_at}</strong>
                  </p>
                  <div className="join-role-sections">
                    <section className="join-rich-section">
                      <div className="section-header">
                        <span className="section-subtitle">{t('JoinFutureWorld.overview.label')}</span>
                        <h3 className="section-title">{t('JoinFutureWorld.overview.title')}</h3>
                      </div>
                      <div className="join-paragraph-stack">
                        {selectedCareer.body.map((paragraph, index) => (
                          <p key={`${selectedCareer.slug}-paragraph-${index}`}>{paragraph}</p>
                        ))}
                      </div>
                    </section>

                    <section className="join-rich-section">
                      <div className="section-header">
                        <span className="section-subtitle">{t('JoinFutureWorld.requirements.label')}</span>
                        <h3 className="section-title">{t('JoinFutureWorld.requirements.title')}</h3>
                      </div>
                      <div className="join-requirement-list">
                        {selectedCareer.requirements.map((item, index) => (
                          <article className="join-requirement-item reveal" style={{ transitionDelay: `${0.08 * (index + 1)}s` }} key={`${selectedCareer.slug}-${item.index}`}>
                            <div className="join-requirement-index">{item.index}</div>
                            <div className="join-requirement-copy">
                              <h4>{item.title}</h4>
                              <p>{item.description}</p>
                            </div>
                          </article>
                        ))}
                      </div>
                    </section>
                  </div>
                </section>
              </div>

              <aside className="join-contact-panel reveal delay-2">
                <span className="join-contact-label">{t('JoinFutureWorld.contact.label')}</span>
                <h3>{t('JoinFutureWorld.contact.title')}</h3>
                <p className="join-contact-description">{t('JoinFutureWorld.contact.description')}</p>

                <div className="join-contact-details">
                  <div className="join-contact-row">
                    <span>{t('JoinFutureWorld.contact.phoneLabel')}</span>
                    <a href={`tel:${selectedCareer.contact_phone}`}>{selectedCareer.contact_phone}</a>
                  </div>

                  <div className="join-contact-row">
                    <span>{t('JoinFutureWorld.contact.emailLabel')}</span>
                    <a href={`mailto:${selectedCareer.contact_email}`}>{selectedCareer.contact_email}</a>
                  </div>
                </div>

                <div className="join-qr-block" aria-label={t('JoinFutureWorld.contact.qrLabel')}>
                  <div className="join-qr-visual">
                    {selectedCareer.qr_image_url ? <img src={selectedCareer.qr_image_url} alt={t('JoinFutureWorld.contact.qrLabel')} className="join-qr-image" /> : <span>{t('JoinFutureWorld.contact.qrEmpty')}</span>}
                  </div>
                  <div className="join-qr-copy">
                    <strong>{t('JoinFutureWorld.contact.qrLabel')}</strong>
                    <span>{selectedCareer.qr_hint || t('JoinFutureWorld.contact.qrEmpty')}</span>
                  </div>
                </div>
              </aside>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

export default JoinFutureWorld;

